import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page } from "@/components/PublicLayout";

export default function JoinPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || sessionStorage.getItem("partner_invite_token") || "";
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["invite", token], queryFn: async () => (await api.get(`/portal/invite/${encodeURIComponent(token)}`)).data, enabled: !!token });

  useEffect(() => { if (token) sessionStorage.setItem("partner_invite_token", token); }, [token]);

  const accept = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post("/portal/case/accept-invite", { token });
      sessionStorage.removeItem("partner_invite_token");
      toast.success("You have joined the care journey.");
      navigate("/portal");
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  if (!token) return <Page title="Join A Care Journey" testId="join-page"><p className="text-ink-500">This link is missing its invitation code. Ask your partner to send the invitation again.</p></Page>;
  if (isLoading) return <Page title="Join A Care Journey" testId="join-page"><p className="text-ink-500" role="status">Checking your invitation…</p></Page>;
  if (!data?.valid) return <Page title="Join A Care Journey" testId="join-page"><p className="surface max-w-xl p-6" data-testid="join-invalid">This invitation is no longer valid. Ask your partner to send a new one from their portal.</p></Page>;

  return (
    <Page title="Join A Care Journey" lede={`${data.inviter_first_name} invited you to walk the care journey together. Each of you watches the video series, then you meet with a counselor as a couple or family.`} testId="join-page">
      <div className="surface max-w-xl p-6" data-testid="join-card">
        {user ? (
          <>
            <p>You are signed in as <strong>{user.first_name} {user.last_name}</strong> ({user.email}).</p>
            {error && <p className="field-error mt-3" role="alert" data-testid="join-error">{error}</p>}
            <button type="button" className="btn-primary mt-5" onClick={accept} disabled={busy} data-testid="join-accept">{busy ? "Joining…" : "Accept And Join"}</button>
          </>
        ) : (
          <>
            <p>Sign in or create your free account to accept. Your invitation stays saved while you do.</p>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <Link to="/sign-up" state={{ from: `/join?token=${token}` }} className="btn-primary" data-testid="join-sign-up">Create My Account</Link>
              <Link to="/sign-in" state={{ from: `/join?token=${token}` }} className="btn-secondary" data-testid="join-sign-in">Sign In</Link>
            </div>
          </>
        )}
      </div>
    </Page>
  );
}
